import {
  Stack,
  Tooltip,
  Typography,
  useTheme,
  StackProps,
} from '@mui/material';

import { GQTaskWatershedProcessStateSimplified } from '@watershed/shared-universal/generated/graphql-schema-types';

import { Plural, Trans } from '@lingui/react/macro';
import { Box, SystemStyleObject } from '@mui/system';

import StatusCompleteIcon from '@watershed/icons/components/StatusComplete';

import assertNever from '@watershed/shared-util/assertNever';
import Logo from '@watershed/ui-core/components/Logo';

import { getTaskStatusTextSimplified } from '@watershed/shared-universal/utils/measurementUtils';

import { Variant } from '@mui/material/styles/createTypography';

import StatusMissingIcon from '@watershed/icons/components/StatusMissing';
import StatusCircleEmptyIcon from '@watershed/icons/components/StatusCircleEmpty';
import StatusStableNewIcon from '@watershed/icons/components/StatusStableNew';

// A soft limit for the number of tasks that can be created from bulk upload flows.
// NOT a comprehensive check; just a sanity check on the frontend.
export const SOFT_TASK_COUNT_LIMIT = 1000;

export const clickableSx: SystemStyleObject = {
  cursor: 'pointer',
};

export const TASK_STATUS_ICON_DEFAULT_SIZE_PX = 16;

export function TaskStatusSimplifiedIcon({
  size = TASK_STATUS_ICON_DEFAULT_SIZE_PX,
  statusSimplified,
  taskCount,
  hideTaskCountLabel = false,
  approvalsEnabledForProject,
}: {
  size?: number;
  statusSimplified: GQTaskWatershedProcessStateSimplified;
  taskCount?: number;
  hideTaskCountLabel?: boolean;
  approvalsEnabledForProject: boolean;
}) {
  const theme = useTheme();
  let Icon;
  let iconColor;
  let title;

  switch (statusSimplified) {
    case 'NotStarted':
      Icon = StatusMissingIcon;
      iconColor = theme.palette.grey20;
      title =
        taskCount !== undefined ? (
          <Plural
            value={taskCount}
            one="This task is not yet started"
            other="These # tasks are not yet started"
          />
        ) : (
          <Trans>This task is not yet started</Trans>
        );
      break;
    case 'InProgress':
      Icon = StatusCircleEmptyIcon;
      iconColor = theme.palette.marigold20;
      title =
        taskCount !== undefined ? (
          <Plural
            value={taskCount}
            one="This task is in progress"
            other="These # tasks are in progress"
          />
        ) : (
          <Trans>This task is in progress</Trans>
        );
      break;
    case 'NeedsApproval':
      Icon = StatusStableNewIcon;
      iconColor = theme.palette.cobalt40;
      title =
        taskCount !== undefined ? (
          <Plural
            value={taskCount}
            one="This task needs approval"
            other="These # tasks need approval"
          />
        ) : (
          <Trans>This task needs approval</Trans>
        );
      break;
    case 'Completed':
      Icon = approvalsEnabledForProject
        ? StatusCompleteIcon
        : StatusStableNewIcon;
      iconColor = approvalsEnabledForProject
        ? theme.palette.success.dark
        : theme.palette.cobalt40;
      title =
        taskCount !== undefined ? (
          <Plural
            value={taskCount}
            one="This task is complete"
            other="These # tasks are complete"
          />
        ) : (
          <Trans>This task is complete</Trans>
        );
      break;
    case 'None':
      return null;
    default:
      assertNever(statusSimplified);
  }

  return (
    <Tooltip title={title}>
      <Stack direction="row" gap={0.5} alignItems="center">
        <Icon color={iconColor} size={size} />
        {taskCount && !hideTaskCountLabel && (
          <Typography
            variant="caption"
            sx={{
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {taskCount}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

export function TaskStatusSimplifiedLabel({
  approvalsEnabledForProject,
  statusSimplified,
  variant = 'caption',
  taskCount = undefined,
  dataTest,
  iconSize = 20,
  spacing = 0.5,
  tooltip,
  textColor,
}: {
  approvalsEnabledForProject: boolean;
  statusSimplified: GQTaskWatershedProcessStateSimplified;
  variant?: Variant;
  taskCount?: number;
  dataTest?: string;
  iconSize?: number;
  spacing?: StackProps['gap'];
  tooltip?: string;
  textColor?: string;
}) {
  return (
    <Stack direction="row" gap={spacing} alignItems="center">
      <TaskStatusSimplifiedIcon
        statusSimplified={statusSimplified}
        size={iconSize}
        approvalsEnabledForProject={approvalsEnabledForProject}
      />
      <Tooltip title={tooltip}>
        <Typography
          variant={variant}
          noWrap
          data-testid={dataTest}
          color={textColor}
        >
          {getTaskStatusTextSimplified(
            statusSimplified,
            approvalsEnabledForProject
          )}{' '}
          {taskCount !== undefined && `(${taskCount})`}
        </Typography>
      </Tooltip>
    </Stack>
  );
}

export function WatershedAssigneeToken({
  variant,
}: {
  variant: 'compact' | string;
}) {
  const typographyVariant = variant === 'compact' ? 'caption' : 'body1';

  return (
    <Stack gap={0.5} direction="row">
      {variant !== 'compact' && (
        <Box
          sx={{
            height: 24,
            width: 24,
            borderRadius: '50%',
            background: (theme) => theme.palette.cobaltGradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Logo color="white" size={20} />
        </Box>
      )}
      <Typography variant={typographyVariant}>
        <Trans
          context="Assignee option label"
          comment="Assigns an object to a Watershed employee"
        >
          Watershed expert
        </Trans>
      </Typography>
    </Stack>
  );
}

export function isTaskComplete(task: {
  statusSimplified: GQTaskWatershedProcessStateSimplified;
}) {
  return (
    task.statusSimplified === GQTaskWatershedProcessStateSimplified.Completed
  );
}
